import React from 'react';

import { IconButton } from 'app/shopify/components/actions/IconButton/IconButton';
import { ToggleIconButton } from 'app/shopify/components/actions/ToggleIconButton/ToggleIconButton';
import { Signals } from 'app/shopify/components/feedback/Signals/Signals';
import { Tag } from 'app/shopify/components/feedback/Tag/Tag';
import { Row } from 'app/shopify/components/layout/Row/Row';
import { Avatar } from 'app/shopify/components/media/Avatar/Avatar';
import { Image } from 'app/shopify/components/media/Image/Image';
import { Box, Text, useTheme } from 'app/shopify/theme';

import { DotsVerticalFilledIcon } from './DotsVerticalFilledIcon';
import { MapPinIcon } from './MapPinIcon';

type ProductTileOrientation = 'vertical' | 'horizontal';

export interface ProductTileProps {
  orientation?: ProductTileOrientation;
  title?: string;
  tags?: [string, string] | string[];
  distanceLabel?: string;
  signalLabel?: string;
  userName?: string;
  userTimeLabel?: string;
  imageUri?: string;
  userAvatarUri?: string;
}

function ProductInfo({
  userName,
  userTimeLabel,
  userAvatarUri,
}: Pick<ProductTileProps, 'userName' | 'userTimeLabel' | 'userAvatarUri'>) {
  return (
    <Row alignItems="center" gap="spacing.general.x2">
      <Avatar size={32} style="rounded" media="image" imageUri={userAvatarUri} />
      <Box minHeight={32} justifyContent="center">
        <Text variant="bodyXSmall" color="text.primary">
          {userName}
        </Text>
        <Text variant="bodyXSmall" color="text.secondary">
          {userTimeLabel}
        </Text>
      </Box>
    </Row>
  );
}

function ProductMedia({
  imageUri,
  isHorizontal,
  signalLabel,
}: Readonly<Pick<ProductTileProps, 'imageUri'> & { isHorizontal: boolean; signalLabel: string }>) {
  return (
    <Box
      width={isHorizontal ? 144 : 181}
      height={isHorizontal ? 144 : 181}
      borderRadius={isHorizontal ? 'radius.md' : 'radius.lg'}
      style={{ overflow: 'hidden' }}
    >
      <Image
        aspectRatio="1:1"
        orientation="landscape"
        imageUri={imageUri}
        width={isHorizontal ? 144 : 181}
        height={isHorizontal ? 144 : 181}
        borderRadius={isHorizontal ? 12 : 16}
      />

      <Box position="absolute" top={isHorizontal ? 12 : 8} left={isHorizontal ? 12 : 8}>
        <Signals label={signalLabel} type="primary" size="default" validation="none" leadingIcon={false} />
      </Box>

      {!isHorizontal ? (
        <Box
          position="absolute"
          top={8}
          right={8}
          width={32}
          height={32}
          borderRadius="radius.full"
          backgroundColor="surface.default"
          alignItems="center"
          justifyContent="center"
        >
          <ToggleIconButton size="small" type="ghost" />
        </Box>
      ) : null}
    </Box>
  );
}

function ProductActions({ isHorizontal }: Readonly<{ isHorizontal: boolean }>) {
  const theme = useTheme();
  if (isHorizontal) {
    return (
      <Row>
        <Box
          width={32}
          height={32}
          borderRadius="radius.full"
          backgroundColor="surface.default"
          alignItems="center"
          justifyContent="center"
        >
          <ToggleIconButton size="small" type="ghost" />
        </Box>
        <IconButton
          variant="ghostNeutral"
          size="small"
          icon={<DotsVerticalFilledIcon size={20} color={theme.colors['text.primary']} />}
        />
      </Row>
    );
  }
  return (
    <IconButton
      variant="ghostNeutral"
      size="medium"
      icon={<DotsVerticalFilledIcon size={24} color={theme.colors['text.primary']} />}
    />
  );
}

function ProductDetails({
  displayedTags,
  distanceLabel,
  isHorizontal,
  title,
  userAvatarUri,
  userName,
  userTimeLabel,
}: Readonly<
  Pick<ProductTileProps, 'distanceLabel' | 'title' | 'userAvatarUri' | 'userName' | 'userTimeLabel'> & {
    displayedTags: string[];
    isHorizontal: boolean;
  }
>) {
  const theme = useTheme();
  return (
    <Box flex={1} minWidth={0} gap="spacing.general.x3">
      <Row alignItems="center">
        <Text
          variant="bodyBase"
          color="text.primary"
          style={{
            fontFamily: theme.fonts.theFuture.bold,
            flex: 1,
          }}
          numberOfLines={2}
        >
          {title}
        </Text>
        <ProductActions isHorizontal={isHorizontal} />
      </Row>

      <Row alignItems="center" flexWrap="wrap" gap="spacing.general.x2">
        <Row gap="spacing.general.x1">
          {displayedTags.map((tag) => (
            <Tag key={tag} label={tag} type="secondary" size="default" inverted={false} />
          ))}
        </Row>

        <Row alignItems="center" gap="spacing.general.x0,5">
          <MapPinIcon size={16} color={theme.colors['text.secondary']} />
          <Text variant="bodyXSmall" color="text.secondary">
            {distanceLabel}
          </Text>
        </Row>
      </Row>

      {isHorizontal ? (
        <ProductInfo userName={userName} userTimeLabel={userTimeLabel} userAvatarUri={userAvatarUri} />
      ) : null}
    </Box>
  );
}

export function ProductTile({
  orientation = 'vertical',
  title = 'Title',
  tags = ['Tag label', 'Tag label'],
  distanceLabel = '1 km',
  signalLabel = 'Nouveaute',
  userName = 'Cafe des habitudes',
  userTimeLabel = 'il y a 2h',
  imageUri,
  userAvatarUri,
}: ProductTileProps) {
  const isHorizontal = orientation === 'horizontal';
  const displayedTags = tags.slice(0, 2);

  return (
    <Box
      width={isHorizontal ? 370 : 181}
      borderRadius={isHorizontal ? 'radius.lg' : undefined}
      borderWidth={isHorizontal ? 1 : 0}
      borderColor={isHorizontal ? 'border.divider' : undefined}
      padding={isHorizontal ? 'spacing.general.x2' : undefined}
      gap="spacing.general.x2"
      flexDirection={isHorizontal ? 'row' : 'column'}
    >
      {!isHorizontal ? (
        <ProductInfo userName={userName} userTimeLabel={userTimeLabel} userAvatarUri={userAvatarUri} />
      ) : null}

      <ProductMedia imageUri={imageUri} isHorizontal={isHorizontal} signalLabel={signalLabel} />
      <ProductDetails
        displayedTags={displayedTags}
        distanceLabel={distanceLabel}
        isHorizontal={isHorizontal}
        title={title}
        userAvatarUri={userAvatarUri}
        userName={userName}
        userTimeLabel={userTimeLabel}
      />
    </Box>
  );
}
